import React from 'react';
import styled from 'styled-components';
import { NextPage, GetServerSideProps } from 'next';
import { Link as StyledLink } from '@makeship/core';

import Container from '../../components/Container';
import Head from '../../components/Head';
import { getPrivacyPolicy, getRefundPolicy, getTermsOfServicePolicy, getShippingPolicy } from '../../api/shop';
import { transformHtml } from '../../utils/htmr';
import { ShopPolicy } from '../../types/common';
import { H1, H5, H6, P2 } from '../../components/Typography';
import { getPropsForPageLoad } from '../../utils/pageLoad';

const PolicyWrapper = styled(Container)`
  padding: 48px 64px 72px 64px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    padding: 24px 16px;
  }
`;

const PolicyP2 = styled(P2)`
  padding-top: 0;
  padding-bottom: 16px;
`;

type PolicyPageProps = {
  handle: string;
  shopPolicy: Shopify.ShopPolicy;
};

const PolicyPage: NextPage<PolicyPageProps> = ({ handle, shopPolicy }: PolicyPageProps) => (
  <>
    <Head title={shopPolicy.title} url={`/policies/${handle}`} />
    <PolicyWrapper>
      {transformHtml(shopPolicy.body, {
        h1: H1,
        h5: H5,
        h6: H6,
        p: PolicyP2,
        a: StyledLink.Primary,
      })}
    </PolicyWrapper>
  </>
);

export const getServerSideProps: GetServerSideProps<PolicyPageProps> = async (ctx) => {
  const { params } = ctx;
  const handle = params ? (params.handle as string) : '';
  let shopPolicy: Shopify.ShopPolicy;

  switch (handle) {
    case ShopPolicy.PrivacyPolicy:
      shopPolicy = await getPrivacyPolicy().then(({ data }) => data.shop.privacyPolicy as Shopify.ShopPolicy);
      break;
    case ShopPolicy.RefundPolicy:
      shopPolicy = await getRefundPolicy().then(({ data }) => data.shop.refundPolicy as Shopify.ShopPolicy);
      break;
    case ShopPolicy.TermsOfService:
      shopPolicy = await getTermsOfServicePolicy().then(({ data }) => data.shop.termsOfService as Shopify.ShopPolicy);
      break;
    case ShopPolicy.ShippingPolicy:
      shopPolicy = await getShippingPolicy().then(
        ({ data }) => (data.shop as ShopifyShop).shippingPolicy as Shopify.ShopPolicy,
      );
      break;
    default:
      return {
        notFound: true,
      };
  }

  if (!shopPolicy) {
    return {
      notFound: true,
    };
  }

  const propsForPageLoad = await getPropsForPageLoad(ctx);

  return {
    props: {
      handle,
      shopPolicy,
      ...propsForPageLoad,
    },
  };
};

export default PolicyPage;
